import styled, { keyframes } from 'styled-components';
import { FaShoppingCart } from 'react-icons/fa';
import { MdPayment, MdOutlineDownloadDone } from 'react-icons/md';
import { GiConfirmed } from 'react-icons/gi';

const pulse = keyframes`
  0% { transform: scale(0.8); opacity: 1; }
  100% { transform: scale(1.8); opacity: 0; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: ${({ horizontal }) => (horizontal ? 'row' : 'column')};
  gap: ${({ horizontal }) => (horizontal ? '0' : '35px')};
  width: ${({ horizontal }) => (horizontal ? '350px' : '150px')};
  align-items: center;
  justify-content: space-between;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: row;
    width: 300px;
    gap: 0;
  }

  @media (max-width: 480px) {
    width: 250px;
  }
`;

const StepWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: ${({ horizontal }) => (horizontal ? 'column' : 'row')};
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const IconWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${({ active }) => (active ? 'rgba(45, 61, 143, 0.1)' : '#fff')};
  border: 2px solid
    ${({ active }) =>
      active ? 'rgba(45, 61, 143, 1)' : 'rgba(45, 61, 143, 0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ active }) =>
    active ? 'rgba(45, 61, 143, 1)' : 'rgba(45, 61, 143, 0.3)'};
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }

  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
  }
`;

const PulseEffect = styled.div`
  position: absolute;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid rgba(45, 61, 143, 0.3);
  animation: ${pulse} 1.5s infinite;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }

  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
  }
`;

const Line = styled.div`
  width: ${({ horizontal }) => (horizontal ? '40px' : '2px')};
  height: ${({ horizontal }) => (horizontal ? '2px' : '40px')};
  background: ${({ active }) =>
    active ? 'rgba(45, 61, 143, 1)' : 'rgba(45, 61, 143, 0.3)'};
  position: absolute;
  ${({ horizontal }) =>
    horizontal
      ? 'left: 50px; top: 50%; transform: translateY(-50%);'
      : 'top: 50px; left: 50%; transform: translateX(-50%);'}
  transition: background 0.3s ease;
  z-index: 1;

  @media (max-width: 768px) {
    width: 30px;
    height: 2px;
    left: 40px;
    top: 50%;
    transform: translateY(-50%);
  }

  @media (max-width: 480px) {
    width: 25px;
    height: 2px;
    left: 35px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const StepTitle = styled.span`
  margin-top: ${({ horizontal }) => (horizontal ? '8px' : '0')};
  font-size: 14px;
  color: ${({ active }) =>
    active ? 'rgba(45, 61, 143, 1)' : 'rgba(45, 61, 143, 0.5)'};
  font-weight: ${({ active }) => (active ? '600' : '400')};
  transition: all 0.3s ease;
  text-align: center;
  width: 80px;

  @media (max-width: 768px) {
    margin-top: 8px;
    font-size: 12px;
    width: 70px;
  }

  @media (max-width: 480px) {
    margin-top: 6px;
    font-size: 10px;
    width: 60px;
  }
`;

const progressLabels = [
  { title: 'Cart', icon: <FaShoppingCart /> },
  { title: 'Payment', icon: <MdPayment /> },
  { title: 'Confirm', icon: <GiConfirmed /> },
  { title: 'Complete', icon: <MdOutlineDownloadDone /> },
];

const CheckoutProgressbar = ({ currentStep = 0, horizontal = false }) => {
  return (
    <Container horizontal={horizontal}>
      {progressLabels.map((step, index) => (
        <StepWrapper key={step.title} horizontal={horizontal}>
          <StepContainer>
            <IconWrapper active={index <= currentStep}>
              {step.icon}
              {index === currentStep && <PulseEffect />}
            </IconWrapper>
            {index < progressLabels.length - 1 && (
              <Line active={index < currentStep} horizontal={horizontal} />
            )}
          </StepContainer>
          <StepTitle active={index <= currentStep} horizontal={horizontal}>
            {step.title}
          </StepTitle>
        </StepWrapper>
      ))}
    </Container>
  );
};

export default CheckoutProgressbar;
